/**
 * Funciones utilitarias para formatear datos de fecha y tiempo
 * 
 * Proporciona formateo consistente para:
 * - Timestamps de mensajes
 * - Fechas en listas de conversaciones  
 * - Agrupación de mensajes por fecha
 */

/**
 * Formatea un timestamp para mostrar en mensajes individuales
 * Formato: "2:30 PM" o "14:30"
 * 
 * @param date - Fecha a formatear
 * @returns String con hora formateada
 */
export const formatMessageTime = (date: Date): string => {
  return date.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });
};

/**
 * Formatea una fecha para mostrar en la lista de conversaciones
 * Lógica: "2:30 PM" (hoy), "Yesterday", "Mon" (esta semana), "Jan 15" (más antiguo)
 * 
 * @param date - Fecha a formatear
 * @returns String con fecha formateada según antigüedad
 */
export const formatConversationTime = (date: Date): string => {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  
  if (days === 0) {
    return formatMessageTime(date);
  } else if (days === 1) {
    return 'Yesterday';
  } else if (days < 7) {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  } else {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  }
};

/**
 * Formatea una fecha para agrupar mensajes por día
 * Usado como separador visual en el chat
 * 
 * @param date - Fecha a formatear
 * @returns "Today", "Yesterday", o "Monday, Jan 15"
 */
export const formatMessageDate = (date: Date): string => {
  const today = new Date();
  const messageDate = new Date(date);
  
  if (messageDate.toDateString() === today.toDateString()) {
    return 'Today';
  }
  
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  if (messageDate.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  }
  
  return messageDate.toLocaleDateString('en-US', { 
    weekday: 'long',
    month: 'short', 
    day: 'numeric' 
  });
};

/**
 * Agrupa mensajes por fecha para mostrar separadores en el chat
 * 
 * @param messages - Array de mensajes a agrupar
 * @returns Array de tuplas [fechaString, mensajesDelDía] ordenadas cronológicamente
 */
export const groupMessagesByDate = (messages: any[]): [string, any[]][] => {
  const groups: { [key: string]: any[] } = {};
  
  // Agrupar mensajes por fecha (string key)
  messages.forEach(message => {
    const dateKey = message.timestamp.toDateString();
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(message);
  });
  
  // Convertir a array y ordenar cronológicamente
  return Object.entries(groups).sort(([a], [b]) => 
    new Date(a).getTime() - new Date(b).getTime()
  );
};